import moment, { Moment } from 'moment';
import { PaymentTag } from 'shared/types/payment';
import { Periodicity } from 'shared/types/periodicity';
import { IOrder, Order } from './order';
import { IProduct, Product } from './product';

export interface IContract {
  id?: number;
  customer_id?: number;
  expiration_date?: string;
  due_date?: string;
  status?: number;
  bank_id: 1 | 2;
  updated_bank_id_at: string;
  created_at?: string;
  updated_at?: string;
  deleted_at?: string;
  periodicity: Periodicity;
  installments?: number;
  origin?: string;
  is_contract_addendum?: number;
  contract_type_id?: number;
  is_managed_account?: number;
  validity_start_date?: string;
  validity_end_date?: string;
  main_contract_id?: number;
  managed_account_document?: string;
  managed_account_trade_name?: string;
  managed_account_phone_number?: string;
  managed_account_email?: string;
  managed_account_alias?: string;
  effective_end_date?: string;
  is_legacy?: string;

  payment_method?: PaymentTag;
  customer_name?: string;
  customer_document?: string;
  orders: IOrder[];
  services?: IProduct[];
  managedAccount: IManagedAccount;
}

export enum ContractStatusMap {
  DEACTIVATED = 'deactivated',
  ACTIVE = 'active',
  CANCELED = 'canceled',
}

export interface IManagedAccount {
  is_managed_account: boolean;
  managed_account_document: string;
  managed_account_trade_name: string;
  managed_account_phone_number: string;
  managed_account_email: string;
  managed_account_alias: string;
}

export class Contract {
  id?: number;
  customer_id?: number;
  expiration_date?: Moment;
  due_date?: Moment;
  status?: number;
  bankId: 1 | 2;
  updated_bank_id_at?: Moment;
  created_at?: Moment;
  updated_at?: Moment;
  deleted_at?: Moment;
  periodicity: Periodicity;
  installments?: number;
  origin?: string;
  is_contract_addendum?: number;
  contract_type_id?: number;
  is_managed_account?: number;
  validity_start_date?: string;
  validity_end_date?: string;
  main_contract_id?: number;
  managed_account_document?: string;
  managed_account_trade_name?: string;
  managed_account_phone_number?: string;
  managed_account_email?: string;
  managed_account_alias?: string;
  effective_end_date?: string;
  is_legacy?: string;

  payment_method?: PaymentTag;
  customer_name?: string;
  customer_document?: string;
  products: Product[] = [];
  orders: Order[] = [];
  managedAccount?: IManagedAccount;

  constructor(data: IContract) {
    this.id = data.id;
    this.customer_id = data.customer_id;
    this.expiration_date = moment(data.expiration_date);
    this.due_date = moment(data.due_date);
    this.status = data.status;
    this.bankId = data.bank_id;
    this.updated_bank_id_at = data.updated_bank_id_at
      ? moment(data.updated_bank_id_at)
      : undefined;
    this.created_at = moment(data.created_at);
    this.updated_at = moment(data.updated_at);
    this.deleted_at = data.deleted_at ? moment(data.deleted_at) : undefined;
    this.periodicity = data.periodicity;
    this.installments = data.installments;
    this.origin = data.origin;
    this.is_contract_addendum = data.is_contract_addendum;
    this.contract_type_id = data.contract_type_id;
    this.is_managed_account = data.is_managed_account;
    this.validity_start_date = data.validity_start_date;
    this.validity_end_date = data.validity_end_date;
    this.main_contract_id = data.main_contract_id;
    this.managed_account_document = data.managed_account_document;
    this.managed_account_trade_name = data.managed_account_trade_name;
    this.managed_account_phone_number = data.managed_account_phone_number;
    this.managed_account_email = data.managed_account_email;
    this.managed_account_alias = data.managed_account_alias;
    this.effective_end_date = data.effective_end_date;
    this.is_legacy = data.is_legacy;

    this.payment_method = data.payment_method;
    this.customer_name = data.customer_name;
    this.customer_document = data.customer_document;
    data.services?.forEach((product) =>
      this.products.push(new Product(product)),
    );
    data.orders?.forEach((order) => this.orders.push(new Order(order)));

    if (data.is_managed_account) {
      this.managedAccount = {
        is_managed_account: this.is_managed_account === 1 ? true : false,
        managed_account_document: this.managed_account_document ?? '',
        managed_account_trade_name: this.managed_account_trade_name ?? '',
        managed_account_phone_number: this.managed_account_phone_number ?? '',
        managed_account_email: this.managed_account_email ?? '',
        managed_account_alias: this.managed_account_alias ?? '',
      };
    }
  }

  get isCreditCard(): boolean {
    return this.bankId === 1;
  }

  get isInvoice(): boolean {
    return this.bankId === 2;
  }

  get statusTag(): ContractStatusMap {
    if (this.deleted_at) {
      return ContractStatusMap.CANCELED;
    } else {
      if (this.status === 1) {
        return ContractStatusMap.ACTIVE;
      } else {
        return ContractStatusMap.DEACTIVATED;
      }
    }
  }

  get selectedPeriodicity() {
    return this.periodicity === 'monthly' ? 'Mensal' : 'Anual';
  }

  get paymentMethodName() {
    return this.bankId === 1 ? 'Cartão de crédito' : 'Boleto Bancário';
  }

  get nextOrderWithInvoice(): Order | undefined {
    if (this.orders) {
      for (const order of this.orders) {
        if (order.statusId !== 2 && order.isInvoice && order.hasInvoiceLink) {
          return order;
        }
      }
    }

    return undefined;
  }

  get installmentsNumber(): number | undefined {
    if (this.periodicity === 'monthly') return undefined;
    if (this.orders.length < 1) return 0;

    return this.orders[this.orders.length - 1]?.cycle;
  }

  get licensePlan(): Product | void {
    return this.products.find(
      (product) => product.isLicense && !product.deleted_at,
    );
  }

  get planName(): string | void {
    const licenseService = this.licensePlan;
    if (licenseService?.plan) {
      return licenseService.plan.name?.split(' - ')[0];
    }
    const first = this.products[0];
    return first?.plan?.name ?? first?.name ?? 'Não licenciável';
  }
}
